
import MainLayout from '../layouts/MainLayout.vue';
import PanelBox from '../components/PanelBox.vue';
import { createNamespacedHelpers } from 'vuex';
import { Component, Vue, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import {
  EAppRoles,
  IAppUser,
  ICustomer,
  IAppRole,
} from '@license-manager/types';
import { GetAppUsersParams } from '../store/licenses';
import vSelect from 'vue-select';
import RoleSelect from '../components/RoleSelect.vue';

const { mapActions, mapState, mapMutations, mapGetters } =
  createNamespacedHelpers('licenses');
const { mapState: mapCompaniesState, mapGetters: mapCompaniesGetters } =
  createNamespacedHelpers('companies');

const licenses = namespace('licenses');
const companies = namespace('companies');
const user = namespace('user');
const approles = namespace('approles');

@Component({
  name: 'ListLicenses',
  components: { PanelBox, MainLayout, vSelect, RoleSelect },
  i18n: {
    // `i18n` option, setup locale info for component
    messages: {
      en: {
        licenseManager: 'Door Assistant License Manager',
        user: 'User',
        firstname: 'Firstname',
        lastname: 'Lastname',
        email: 'Email',
        company: 'Company',
        role: 'Role',
        customercode: 'Customercode',
        customer: 'Customer',
        createdAt: 'Created at',
        changedAt: 'Changed at',
        lastLogin: 'Last login',
        offlineRevalidTime: 'Offline validity time',
        expires: 'Expires at',
        edit: 'Edit',
        add_new: 'Add license',
        licenses: 'Licenses',
        userIsConfirmed: 'Registration confirmed',
        appVersion: 'App Version',
      },
      de: {
        licenseManager: 'Door Assistant Lizenzverwaltung',
        user: 'Benutzer',
        firstname: 'Vorname',
        lastname: 'Nachname',
        email: 'Email',
        company: 'Firma',
        role: 'Rolle',
        customercode: 'Kundencode',
        customer: 'Kunde',
        createdAt: 'Erstellt am',
        changedAt: 'Geändert am',
        lastLogin: 'Letzter Login',
        offlineRevalidTime: 'Offline Gültigkeitszeit',
        expires: 'Gültig bis',
        edit: 'Bearbeiten',
        add_new: 'Neue Lizenz',
        licenses: 'Lizenzen',
        userIsConfirmed: 'Registrierung bestätigt',
        appVersion: 'App version',
      },
    },
  },
})
export default class ListLicenses extends Vue {
  @licenses.Action('getAppusers') getAppusers: (
    params?: GetAppUsersParams
  ) => Promise<void>;

  @licenses.Mutation('setSort') setSort: ({ field, asc }) => void;

  @licenses.State('isLoading') isLoading: boolean;
  @licenses.State('appusers') appusers: IAppUser[];
  @licenses.State('sort') sort: any;

  @licenses.Getter('getAppUsersSorted') getAppUsersSorted: (
    field?,
    asc?
  ) => IAppUser[];

  @licenses.Getter('totalCount') totalCountOfLicenses: number;

  @companies.Getter('alphaSorted') companiesSorted: ICustomer[];
  @companies.Action('loadCompanies') loadCompanies: () => Promise<any>;

  async mounted() {
    this.loadCompanies();
    this.loadRoles({ includeRights: false, loadAllRoles: true });
    if (!this.appusers) {
      this.setQueryParamsByUrlAndLoad();
    }
  }

  get tableHeaders() {
    const addCustomer = this.$ability.can('manage', 'Customer');

    return [
      { name: 'user', sortName: 'username' },
      { name: 'firstname', sortName: 'firstname' },
      { name: 'lastname', sortName: 'lastname' },
      { name: 'email', sortName: 'email' },
      addCustomer ? { name: 'customer' } : null,
      { name: 'company', sortName: 'company' },
      { name: 'role', sortName: 'role' },
      { name: 'lastDayOfValidity', sortName: 'expiresAt' },
      /*addCustomer ? { name: 'customercode', sortName: 'customer.hc' } : null,*/
      /*addCustomer ? { name: 'customer', sortName: 'customer.name' } : null,*/
      { name: 'createdAt', sortName: 'createdAt' },
      { name: 'changedAt', sortName: 'updatedAt' },
      { name: 'lastLogin', sortName: 'lastLogin' },
      /*{ name: 'offlineRevalidTime', sortName: 'offlineRevalidationTime' },*/

      addCustomer ? { name: 'appVersion', sortName: 'appVersion' } : null,
      { name: 'userIsConfirmed', sortName: 'userIsConfirmed' },
    ].filter((e) => Boolean(e));
  }

  public take;
  like = null;
  currentPage;
  index;
  appRoleId = null;
  customerId;
  validity = null;

  getQueryParams(page) {
    const params = {};

    if (this.take) {
      params['take'] = this.take;
    }

    if (this.like) {
      params['like'] = this.like;
    }

    if (this.appRoleId) {
      params['appRoleId'] = this.appRoleId;
    }

    if (this.customerId) {
      params['customerId'] = this.customerId;
    }

    if (this.validity) {
      params['validity'] = this.validity;
    }

    if (page) {
      params['page'] = page;
    }

    return params;
  }

  @approles.Getter('getAllRoles')
  getAllRoles: (customerId?: number) => IAppRole[];

  @approles.Action('loadRoles')
  loadRoles: (params: {
    includeRights: boolean;
    loadAllRoles: boolean;
    customerId?: number;
  }) => Promise<void>;


  @Watch('$route.query')
  async setQueryParamsByUrlAndLoad() {
    this.take = this.$route.query['take'] ?? 50;
    this.currentPage = this.$route.query['page'] ?? 1;
    this.like = (this.$route.query['like'] as string) ?? '';
    this.appRoleId = (this.$route.query['appRoleId'] as string) ?? '';
    this.customerId = (this.$route.query['customerId'] as string) ?? '';
    this.validity = (this.$route.query['validity'] as string) ?? null;

    await this.getAppusers({
      appRoleId: this.appRoleId,
      take: this.take,
      skip: this.skip(),
      like: this.like,
      customerId: this.customerId,
      validity: this.validity,
    });
  }

  skip() {
    return (this.currentPage - 1) * this.take;
  }

  hasNextPage() {
    return this.currentPage + 1 <= this.pages.length;
  }

  hasPrevPage() {
    return this.currentPage > 1;
  }

  isActivePage(index) {
    return index + 1 == this.currentPage;
  }

  get approles() {
    return EAppRoles;
  }

  startSearch({
    search = this.like,
    appRoleId = this.appRoleId,
    customerId = this.customerId,
    validity = this.validity,
  }: {
    search?: string;
    appRoleId?: string;
    customerId?: number;
    validity?: string;
  }) {
    const query = {};

    query['like'] = search;
    query['appRoleId'] = appRoleId;
    query['customerId'] = customerId;

    if (validity != '') {
      query['validity'] = validity;
    }

    this.$router.replace({
      path: this.$route.path,
      query,
    });
  }

  get pages() {
    const length = Math.ceil(this.totalCountOfLicenses / this.take);
    return Array.from({ length: length });
  }
}
